import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCreateClient, useStudioClients, useStudioContour } from '@/api/studio';
import type { ClientContact, ContourInfo, LegalValues } from '@/api/types';
import { RequisitesFields } from '@/components/Requisites';
import { Dialog, Loading, PageHeader, Status } from '@/components/ui';
import { QueryPending } from '@/components/QueryState';
import { formatMoney, formatWhen } from '@/lib/format';
import { toast } from '@/lib/toast';

const stageLabel = { lead: 'Лид', proposal: 'Предложение', active: 'Работаем', paused: 'Пауза', former: 'Бывший' } as const;
const stageTone = { lead: 'neutral', proposal: 'waiting', active: 'done', paused: 'neutral', former: 'neutral' } as const;

// One installation serves one contour: what it means for the client, in a line.
const contourLabel = { ru: ['Россия', 'Точка, 54-ФЗ, данные в РФ'], main: ['Основной', 'Клиенты вне РФ, оплата через Paddle'] } as const;

function NewClientDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data: contour } = useStudioContour();
  return (
    <Dialog open={open} onClose={onClose} title="Новый клиент" wide>
      {contour ? <NewClientForm contour={contour} onClose={onClose} /> : <Loading />}
    </Dialog>
  );
}

/** Requisites come from the contour's schema: ИНН/КПП in RU, country/VAT id/address in main. */
export function NewClientForm({ contour, onClose }: { contour: ContourInfo; onClose: () => void }) {
  const create = useCreateClient();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [legal, setLegal] = useState<LegalValues>({});
  const [contacts, setContacts] = useState<{ name: string; email: string; role: ClientContact['role'] }[]>([{ name: '', email: '', role: 'owner' }]);
  const valid = name.trim() && contacts.some((c) => c.name.trim() && /.+@.+\..+/.test(c.email));

  const [title, hint] = contourLabel[contour.code];

  return (
      <form
        className="space-y-4 p-5"
        onSubmit={(e) => {
          e.preventDefault();
          if (!valid) return;
          create.mutate(
            { name: name.trim(), legal, contour: contour.code, contacts: contacts.filter((c) => c.name.trim() && c.email.trim()) },
            {
              onSuccess: (c) => {
                toast(c.invited === false ? 'Клиент заведён, но приглашение не ушло — отправьте его ещё раз из карточки клиента' : 'Клиент заведён, приглашение в кабинет отправлено');
                onClose();
                navigate(`/studio/clients/${c.id}`);
              },
            },
          );
        }}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="block text-sm sm:col-span-2">
            <span className="mb-1.5 block text-muted">Название для кабинета</span>
            <input className="field" value={name} onChange={(e) => setName(e.target.value)} placeholder="Как клиента зовут в студии, например «Карандаш»" autoFocus />
          </label>
          <p className="text-xs text-muted sm:col-span-2">Реквизиты можно заполнить позже: клиент допишет их в кабинете.</p>
          <RequisitesFields legal={contour.legal} values={legal} onChange={(k, v) => setLegal((x) => ({ ...x, [k]: v }))} markRequired={false} />
          <p className="text-sm sm:col-span-2">
            <span className="text-muted">Контур — где живут данные и деньги клиента: </span>
            <span className="font-medium">{title}</span>
            <span className="text-muted"> ({hint})</span>
          </p>
        </div>

        <div>
          <div className="mb-1.5 text-sm text-muted">Кого пригласить в кабинет</div>
          <div className="space-y-2">
            {contacts.map((c, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_140px_32px] gap-2">
                <input className="field" placeholder="Имя" value={c.name} onChange={(e) => setContacts((cs) => cs.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
                <input className="field" placeholder="Почта" type="email" value={c.email} onChange={(e) => setContacts((cs) => cs.map((x, j) => (j === i ? { ...x, email: e.target.value } : x)))} />
                <select className="field" value={c.role} onChange={(e) => setContacts((cs) => cs.map((x, j) => (j === i ? { ...x, role: e.target.value as ClientContact['role'] } : x)))}>
                  <option value="owner">Владелец</option>
                  <option value="member">Сотрудник</option>
                  <option value="accountant">Бухгалтер</option>
                </select>
                <button type="button" className="btn-ghost size-8 self-center px-0" aria-label="Убрать" onClick={() => setContacts((cs) => cs.filter((_, j) => j !== i))} disabled={contacts.length === 1}>
                  <Trash2 className="size-4" />
                </button>
              </div>
            ))}
          </div>
          <button type="button" className="btn-ghost mt-2" onClick={() => setContacts((cs) => [...cs, { name: '', email: '', role: 'member' }])}>
            <Plus className="size-4" /> Ещё человек
          </button>
          <p className="mt-2 text-xs text-muted">Бухгалтер видит только «Деньги» и «Документы». Приглашение уходит на почту, вход — по ссылке.</p>
        </div>

        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Отмена
          </button>
          <button className="btn-primary" disabled={!valid || create.isPending}>
            Создать и пригласить
          </button>
        </div>
        {create.error && <p className="text-right text-sm text-overdue">{(create.error as Error).message}</p>}
      </form>
  );
}

export function StudioClients() {
  const clientsQ = useStudioClients();
  const { data } = clientsQ;
  const [open, setOpen] = useState(false);
  return (
    <>
      <PageHeader
        title="Клиенты"
        actions={
          <button className="btn-primary" onClick={() => setOpen(true)}>
            <Plus className="size-4" /> Новый клиент
          </button>
        }
      />
      {!data ? (
        <QueryPending query={clientsQ} />
      ) : (
        <div className="overflow-x-auto"><table className="w-full min-w-[720px] text-left text-sm">
          <thead className="text-muted">
            <tr className="border-b border-line">
              <th className="py-2 pr-4 font-normal">Клиент</th>
              <th className="py-2 pr-4 font-normal">Стадия</th>
              <th className="py-2 pr-4 text-right font-normal">Проектов</th>
              <th className="py-2 pr-4 text-right font-normal">В месяц</th>
              <th className="py-2 pr-4 text-right font-normal">Ждёт оплаты</th>
              <th className="py-2 pr-4 font-normal">Ждут нас / ждём их</th>
              <th className="py-2 font-normal">Последний контакт</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {data.map((c) => (
              <tr key={c.id} className="hover:bg-panel">
                <td className="py-3 pr-4">
                  <Link to={`/studio/clients/${c.id}`} className="font-medium hover:underline">
                    {c.name}
                  </Link>
                  <div className="text-xs text-muted">
                    {c.legalName}, {c.contour === 'ru' ? 'контур РФ' : 'основной контур'}
                  </div>
                </td>
                <td className="py-3 pr-4">
                  <Status tone={stageTone[c.stage]}>{stageLabel[c.stage]}</Status>
                </td>
                <td className="num py-3 pr-4 text-right">{c.projects}</td>
                <td className="num py-3 pr-4 text-right">{c.mrr.amountMinor ? formatMoney(c.mrr) : '—'}</td>
                <td className="num py-3 pr-4 text-right">
                  {c.receivable?.amountMinor ? formatMoney(c.receivable) : '—'}
                  {c.overdue?.amountMinor ? <div className="text-xs text-overdue">просрочено {formatMoney(c.overdue)}</div> : null}
                </td>
                <td className="py-3 pr-4">
                  <span className={c.waitingOnUs ? 'font-semibold text-overdue' : 'text-faint'}>{c.waitingOnUs}</span>
                  <span className="text-faint"> / </span>
                  <span className={c.waitingOnClient ? 'text-waiting' : 'text-faint'}>{c.waitingOnClient}</span>
                </td>
                <td className="py-3 text-muted">{c.lastContactAt ? formatWhen(c.lastContactAt) : 'не было'}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}
      <NewClientDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}
